import { html, LitElement, css} from 'lit';
import {property, customElement} from 'lit/decorators.js';
import { GetFormattedDateNoYear } from '../../../src/utils/helpers';
import { WeatherModel } from '../../../src/models/weather-model';
import { SvgIcons } from '../../voyage-svg-icons/src/voyage-svg-icons';
import { WeatherControlMode } from '../../voyage-vessel-weather-control/src/voyage-vessel-weather-control';
import { UiStateModel, UiStateModelParam } from '../../../src/models/ui-state-model';
import { MenuComponent } from '../../../vessel/src/main-app';
import { formatSpotWeatherElement } from '../../../src/utils/weather-utils';
import '../../voyage-spinner/src/voyage-spinner';

const WEATHER_TIME_BUCKETS = 60*60*1000;

export interface MapCoordinates {
    lat: string,
    lon: string,
    x: number, 
    y: number
}

@customElement('voyage-vessel-spot-weather-control')
export class VoyageVesselSpotWeatherControl extends LitElement
{
    @property({type: Number}) playerTime: number|null = null;
    @property({type: Object}) mapCoordinates: MapCoordinates|null = null;
    @property({type: Boolean}) selectMap: boolean = false;
    @property({type: Boolean}) shown: boolean = false;

    @property({attribute: false}) private _currentSpotWeather: Map<string, number[]>|null = null;
    @property({attribute: false}) private _currentTime: number|null = null;
    @property({attribute: false}) private _downloading: boolean = false;
    @property({attribute: false}) _visibleCoordinates: MapCoordinates|null = null;
    private weatherByTimestamp: { timestamp: number; weather: { element: string; value: number[]; }[]; }[] = [];
    private tooltip: HTMLDivElement|null = null;

    static get styles() {
        return [
            css`
                .container {
                    display: none;
                }

                .container[shown] {
                    display: flex;
                    flex-direction: column;
                    width: 283px;
                    background: rgba(53, 53, 53, 0.6);
                    pointer-events: none;
                    margin-left: auto;
                    font-family: MaerskText;
                    font-size: 14px;
                    color: var(--neutrals-grey-10, grey);;
                }

                #header {
                    height: 95.5px;
                    border-bottom: 1.5px solid var(--neutrals-grey-07, grey);
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    grid-column-gap: 10px;
                    grid-row-gap: 6px;
                    place-content: center;
                }

                #main {
                    height: 595px;
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    grid-column-gap: 10px;
                    grid-row-gap: 6px;
                    place-content: center;
                }

                #info {
                    height: 50px;
                    display: grid;
                    place-content: center;
                }

                .label {
                    text-align: end;
                    font-weight: bold;
                }

                voyage-spinner {
                    height: 25px;
                    width: 25px;
                    margin-bottom: 5px;
                    --voyage-spinner-stroke-width: 3px;
                    --voyage-spinner-bg-color: var(--neutrals-grey-07, grey);
                }

                #selectMapButton {
                    pointer-events: auto;
                    cursor: pointer;
                    display: grid;
                    grid-template-columns: auto auto;
                    grid-column-gap: 5px;
                    place-content: center;
                    width: 189px;
                    height: 31px;
                    background: var(--neutrals-grey-10, grey);
                    color: var(--neutrals-grey-02, grey);
                    border: 1px solid var(--neutrals-grey-06, grey);
                    border-radius: 2.5px;
                }

                #selectMapButton[selected] {
                    background: var(--neutrals-grey-04, grey);
                }

                #selectMapButton > svg {
                    color: var(--neutrals-grey-04, grey);
                }
        `];
    }

    render() {
        return html`
        <div data-test="spot-weather-section" class="container" ?shown=${this.shown}>
            <div id="info">
                ${this._downloading ? html`<voyage-spinner></voyage-spinner>` : html`
                    <div id="selectMapButton" ?selected=${this.selectMap} @click=${() => this.onSelectMap()}>${SvgIcons.customSpotWeatherIcon} Activate Spot Weather</div>
                `}
            </div>
            <div data-test="spot-weather-input" id="header">
                <div class="label">LATITUDE</div><div id="Latitude" class="value">${this._visibleCoordinates ? this._visibleCoordinates.lat : ""}</div>
                <div class="label">LONGITUDE</div><div id="Longitude" class="value">${this._visibleCoordinates ? this._visibleCoordinates.lon : ""}</div>
                <div class="label">DATE</div><div id="Date" class="value">${this._currentTime !== null ? `${GetFormattedDateNoYear(this._currentTime)} UTC` : ""}</div>
            </div>
            <div data-test="spot-weather-result" id="main">
                <div class="label">PRESSURE</div><div id="Pressure" class="value">${formatSpotWeatherElement("Pressure", this._currentSpotWeather)}</div>
                <div class="label">WIND 10m</div><div id="Wind10M" class="value">${formatSpotWeatherElement("Wind10M", this._currentSpotWeather)}</div>
                <div class="label">WIND 50m</div><div id="Wind50M" class="value">${formatSpotWeatherElement("Wind50M", this._currentSpotWeather)}</div>
                <div class="label">WIND GUSTS 10m</div><div id="WindGusts10M" class="value">${formatSpotWeatherElement("WindGusts10M", this._currentSpotWeather)}</div>
                <div class="label">WIND GUSTS 50m</div><div id="WindGusts50M" class="value">${formatSpotWeatherElement("WindGusts50M", this._currentSpotWeather)}</div>
                <div class="label">RISK WIND SPEED</div><div id="RiskWindSpeed" class="value">${formatSpotWeatherElement("RiskWindSpeed", this._currentSpotWeather)}</div>
                <div class="label" style="font-weight: 100">SEA STATE:</div><div class="value"></div>
                <div class="label">- WAVE</div><div id="Wave" class="value">${formatSpotWeatherElement("Wave", this._currentSpotWeather)}</div>
                <div class="label">- SWELL</div><div id="Swell" class="value">${formatSpotWeatherElement("Swell", this._currentSpotWeather)}</div>
                <div class="label">RISK WAVE HEIGHT</div><div id="RiskWaveHeight" class="value">${formatSpotWeatherElement("RiskWaveHeight", this._currentSpotWeather)}</div>
                <div class="label">CURRENT</div><div id="Currents" class="value">${formatSpotWeatherElement("Currents", this._currentSpotWeather)}</div>
                <div class="label">TEMPERATURE</div><div id="AirTemperature" class="value">${formatSpotWeatherElement("AirTemperature", this._currentSpotWeather)}</div>
                <div class="label">PRECIPITATION</div><div id="Precipitation" class="value">${formatSpotWeatherElement("Precipitation", this._currentSpotWeather)}</div>
                <div class="label">VISIBILITY</div><div id="Visibility" class="value">${formatSpotWeatherElement("Visibility", this._currentSpotWeather)}</div>
                <div class="label">SEAWATER TEMP.</div><div id="SeaWaterTemperature" class="value">${formatSpotWeatherElement("SeaWaterTemperature", this._currentSpotWeather)}</div>
                <div class="label">WEATHER</div><div id="WeatherCode" class="value">${formatSpotWeatherElement("WeatherCode", this._currentSpotWeather)}</div>
                <div class="label">ICING</div><div id="Icing" class="value">${formatSpotWeatherElement("Icing", this._currentSpotWeather)}</div>
                <div class="label">500hPa</div><div id="HPa500" class="value">${formatSpotWeatherElement("HPa500", this._currentSpotWeather)}</div>
                <div class="label">ICE CONC.</div><div id="IceConcentration" class="value">${formatSpotWeatherElement("IceConcentration", this._currentSpotWeather)}</div>
                <div class="label">RELATIVE HUMIDITY</div><div id="RelativeHumidity" class="value">${formatSpotWeatherElement("RelativeHumidity", this._currentSpotWeather)}</div>
                <div class="label">FREEZING LEVEL</div><div id="FreezingLevel" class="value">${formatSpotWeatherElement("FreezingLevel", this._currentSpotWeather)}</div>
                <div class="label">DEW POINT</div><div id="DewPoint" class="value">${formatSpotWeatherElement("DewPoint", this._currentSpotWeather)}</div>
                <div class="label">SALINITY</div><div id="SeaWaterSalinity" class="value">${formatSpotWeatherElement("SeaWaterSalinity", this._currentSpotWeather)}</div>
            </div>
        </div>
        `;
    }

    constructor() {
        super();

        this.gatherWeather = this.gatherWeather.bind(this);
        this.updateCurrentWeather = this.updateCurrentWeather.bind(this);
    }

    onSelectMap() {
        this.selectMap = !this.selectMap;

        if (this.selectMap) {
            this.dispatchTooltipState(true);
        } else {
            this.dispatchTooltipState(false);
        }
    }

    updated(changedProperties: Map<string | number | symbol, unknown>) {
        const menuComponent: MenuComponent = <MenuComponent>(UiStateModel!.getValue(UiStateModelParam.MainMenuComponent));
        const weatherControlMode: WeatherControlMode = <WeatherControlMode>(UiStateModel!.getValue(UiStateModelParam.SelectedWeatherControlComponent));

        if (changedProperties.has("mapCoordinates") && (menuComponent === "showWeather" && weatherControlMode === "showSpot")) {
            if (this.selectMap) {
                this._visibleCoordinates = this.mapCoordinates;
                this.selectMap = false;
                this._downloading = true;
                this._currentSpotWeather = null;
                this._currentTime = null;
                this.dispatchTooltipState(false);
                this.dispatchEvent(new CustomEvent("spot-weather-set", { detail: { coordinates: this.mapCoordinates }, bubbles: true, composed: true } ));

                this._getWeather();
            }
        }
        if (changedProperties.has("playerTime")) {
            this.updateCurrentWeather();
        }

        super.updated(changedProperties);
    }

    updateCurrentWeather() {
        if (this.weatherByTimestamp.length < 1) return;
         
        let currentIndex = -1;
        
        for (let index = 0; index < this.weatherByTimestamp.length; index++) {
            if (this.weatherByTimestamp[index].timestamp <= this.playerTime!) currentIndex = index;
        }

        this._currentSpotWeather = new Map(this.weatherByTimestamp[currentIndex].weather.map(i => [i.element, i.value]));
        this._currentTime = this.weatherByTimestamp[currentIndex].timestamp;
    }

    getNow() {
        return Date.now();
    }

    dispatchTooltipState(enabled: boolean) {
        this.dispatchEvent(new CustomEvent('spot-weather-tooltip', {bubbles: true, composed: true, detail: { enable: enabled }}));
    }

    async gatherWeather() {
        if (!this.mapCoordinates || !this.playerTime) return;
        
        const now = this.getNow();
        const until = now + WEATHER_TIME_BUCKETS*24*10; // get weather ahead for 10 days (max forecast lenght)

        // Compute relevant times and get ship positions
        const timestamps = [ this.getTimeBucket(now) ];
        while (timestamps[timestamps.length-1] + WEATHER_TIME_BUCKETS < until)
        {
            timestamps.push(timestamps[timestamps.length-1] + WEATHER_TIME_BUCKETS);
        }

        const locationAndTimes = [];

        for (const timestamp of timestamps)
        {
            const coords : [number, number] = [this.mapCoordinates.x, this.mapCoordinates.y];

            locationAndTimes.push({
                timestamp: new Date(timestamp).toISOString(),
                coordinates : coords
            });
        }

        
        // Gather weather information
        const weatherPoints = await WeatherModel!.getMultiSpotWeather(locationAndTimes);
        const weatherByTimestampLocal = [];
        for (const weatherPoint of weatherPoints)
        {
            const wpTimeStamp : string | undefined = weatherPoint.properties.timestamp; 
            const timestamp = new Date(wpTimeStamp!).getTime();
            weatherByTimestampLocal.push({
                timestamp,
                weather: weatherPoint.properties.weather
            });
        
        }

        this.weatherByTimestamp = weatherByTimestampLocal;
    }

    getTimeBucket(timestamp: number) {
        return Math.floor(timestamp / WEATHER_TIME_BUCKETS) * WEATHER_TIME_BUCKETS;
    }

    private async _getWeather() {
        try {
            await this.gatherWeather();
            this._downloading = false;
            this.updateCurrentWeather();
        } catch (error) {
            console.error(error)
            this._downloading = false;
        }
    }
}
